// Выкатки и проверки восстановления (contracts/api.md; FR-042, FR-043б), проверка состояния;
// экраны сопровождения 002 (specs/002-platform/contracts/api.md): состояние, фоновые задачи,
// технический журнал, оповещения, копии, метрики.
import { z } from '../kernel/zod.ts';
import { JobTypeSchema } from '../jobs/catalog.ts';
import { OPERATION_PATTERN, TimestampInputSchema, normalizeOperation } from '../kernel/common.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { PageQuerySchema, pageSchema, sortSchema } from '../kernel/page.ts';
import { AlertKindSchema, AlertSchema, AlertStateSchema } from '../observability/alert.ts';
import { DeploymentSchema, RestoreCheckSchema } from '../observability/deployment.ts';
import { JobSchema, JobStateSchema } from '../observability/job.ts';
import { LogEntrySchema, LogLevelSchema, LogModuleSchema } from '../observability/log.ts';
import { BackupResultSchema } from '../observability/status.ts';

/** GET /api/ops/deployments: выкатки и последние проверки восстановления, новые сверху. */
export const DeploymentsResponseSchema = z.object({
  deployments: z.array(DeploymentSchema),
  restoreChecks: z.array(RestoreCheckSchema),
});
export type DeploymentsResponse = z.infer<typeof DeploymentsResponseSchema>;

/** GET /api/health: процесс жив. Без версии и данных — адрес открыт всем. */
export const HealthResponseSchema = z.object({ status: z.literal('ok') });
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/**
 * GET /api/health/ready: база отвечает, пульсы обработчиков не старше 3 минут — 200 «ready»;
 * иначе 503 «not_ready» без причины (FR-037, FR-038).
 */
export const ReadyResponseSchema = z.object({ status: z.literal('ready') });
export type ReadyResponse = z.infer<typeof ReadyResponseSchema>;
export const NotReadyResponseSchema = z.object({ status: z.literal('not_ready') });

/** GET /api/ops/metrics — текстовый формат Prometheus (FR-034, FR-035). */
export const METRICS_CONTENT_TYPE = 'text/plain; version=0.0.4; charset=utf-8';

/** Номер операции в поиске: с дефисами, строчными, O вместо нуля — находится (FR-032). */
export const OperationQuerySchema = z
  .string()
  .trim()
  .transform(normalizeOperation)
  .pipe(z.string().regex(OPERATION_PATTERN, { error: fieldMessages.operation }));

/** Код ошибки из каталога в фильтре: SYS-001. */
export const ErrorCodeQuerySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2,5}-\d{3}$/, { error: fieldMessages.errorCode });

const periodInOrder = (v: { from?: string | undefined; to?: string | undefined }): boolean =>
  v.from === undefined || v.to === undefined || Date.parse(v.from) <= Date.parse(v.to);

const PeriodSchema = z.object({
  from: TimestampInputSchema.optional(),
  to: TimestampInputSchema.optional(),
});

/** GET /api/ops/jobs: поиск по номеру операции, тип, состояние, по времени создания (FR-047). */
export const jobSortFields = ['createdAt'] as const;
export const JobsQuerySchema = z.object({
  ...PageQuerySchema.shape,
  q: OperationQuerySchema.optional(),
  type: JobTypeSchema.optional(),
  state: JobStateSchema.optional(),
  sort: sortSchema(jobSortFields).default('-createdAt'),
});
export type JobsQuery = z.input<typeof JobsQuerySchema>;
export const JobsPageSchema = pageSchema(JobSchema);

/**
 * POST /api/ops/jobs/{id}/cancel: состояние, которое видел пользователь на карточке. Видел
 * «упала», а задачу уже повторили и она ждёт, — отказ OPS-002 «ждёт выполнения»: из двух
 * одновременных действий выполняется первое (FR-008а). Тело необязательно: без тела Fastify
 * передаёт null.
 */
export const CancelJobBodySchema = z
  .object({ expectedState: JobStateSchema.extract(['queued', 'failed']).optional() })
  .nullish();
export type CancelJobBody = z.input<typeof CancelJobBodySchema>;

/** Записей технического журнала в карточке задачи — не больше. */
export const JOB_LOG_LIMIT = 200;

/** GET /api/ops/jobs/{id}: задача и записи технического журнала по её номеру операции. */
export const JobDetailSchema = z.object({
  job: JobSchema,
  /** Новые сверху, не больше JOB_LOG_LIMIT. */
  log: z.array(LogEntrySchema).max(JOB_LOG_LIMIT),
});
export type JobDetail = z.infer<typeof JobDetailSchema>;

/**
 * GET /api/ops/log: номер операции, код, модуль, уровень не ниже, период (по умолчанию — сутки,
 * не больше срока хранения журнала: иначе REC-007), по времени (FR-032).
 */
export const logSortFields = ['at'] as const;
export const LogQuerySchema = z
  .object({
    ...PageQuerySchema.shape,
    ...PeriodSchema.shape,
    operation: OperationQuerySchema.optional(),
    code: ErrorCodeQuerySchema.optional(),
    module: LogModuleSchema.optional(),
    level: LogLevelSchema.optional(),
    sort: sortSchema(logSortFields).default('-at'),
  })
  .refine(periodInOrder, { path: ['to'], error: fieldMessages.period });
export type LogQuery = z.input<typeof LogQuerySchema>;
export const LogPageSchema = pageSchema(LogEntrySchema).extend({
  /** Всего найдено по условиям. */
  total: z.number().int().nonnegative(),
  /**
   * Срок хранения журнала в днях (retention.logDays): экран пишет его в пояснении и в быстром
   * периоде, а не зашитое число (принцип VIII, FR-031).
   */
  retentionDays: z.number().int().positive(),
});
export type LogPage = z.infer<typeof LogPageSchema>;

/**
 * GET /api/ops/alerts: действующие или история за срок хранения (retention.alertsDays, FR-045);
 * вид, период, по времени начала — новые или старые сверху.
 */
export const alertSortFields = ['startedAt'] as const;
export const AlertsQuerySchema = z
  .object({
    ...PageQuerySchema.shape,
    ...PeriodSchema.shape,
    state: AlertStateSchema.optional(),
    kind: AlertKindSchema.optional(),
    sort: sortSchema(alertSortFields).default('-startedAt'),
  })
  .refine(periodInOrder, { path: ['to'], error: fieldMessages.period });
export type AlertsQuery = z.input<typeof AlertsQuerySchema>;
export const AlertsPageSchema = pageSchema(AlertSchema).extend({
  /** Срок истории закрытых оповещений, дней (retention.alertsDays, FR-045). */
  historyDays: z.number().int().positive(),
});
export type AlertsPage = z.infer<typeof AlertsPageSchema>;

/** GET /api/ops/backups: итог копии и период (FR-048). */
export const BackupsQuerySchema = z
  .object({
    ...PageQuerySchema.shape,
    ...PeriodSchema.shape,
    result: BackupResultSchema.optional(),
  })
  .refine(periodInOrder, { path: ['to'], error: fieldMessages.period });
export type BackupsQuery = z.input<typeof BackupsQuerySchema>;
